import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { getErpBranchDetails } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";

// Create styles - Use Helvetica as default font
const styles = StyleSheet.create({
  page: {
    padding: 5,
    fontFamily: "Helvetica",
  },
  container: {
    flex: 1,
    border: "1pt solid #000000",
    borderRadius: 8,
    padding: 8,
  },
  header: {
    alignItems: "center",
    marginBottom: 5,
  },
  companyTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#8c1d1a",
    marginBottom: 2,
  },
  mobileText: {
    fontSize: 9,
    marginBottom: 1,
  },
  titleSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
    paddingBottom: 3,
    borderBottom: "1pt solid #000000",
  },
  titleText: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 0,
  },
  gstText: {
    fontSize: 9,
    fontWeight: "bold",
  },
  sellerSection: {
    marginBottom: 5,
  },
  sellerText: {
    fontSize: 10,
    marginBottom: 2,
    lineHeight: 1.3,
  },
  sellerAddress: {
    fontSize: 10,
    marginBottom: 2,
  },
  sellerMobile: {
    fontSize: 10,
    marginBottom: 2,
  },
  amountInWords: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 8,
  },
  table: {
    marginTop: 5,
    marginLeft: 0,
    marginRight: 0,
    border: "1pt solid #000000",
    marginBottom: 5,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #000000",
    fontSize: 9,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    fontSize: 9,
    borderTop: "1pt solid #000000",
  },
  tableCol1: {
    width: "8%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol2: {
    width: "25%",
    borderRight: "1pt solid #000000",
    padding: 4,
  },
  tableCol3: {
    width: "10%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol4: {
    width: "15%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol5: {
    width: "15%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol6: {
    width: "12%",
    borderRight: "1pt solid #000000",
    padding: 4,
    textAlign: "center",
  },
  tableCol7: {
    width: "15%",
    padding: 4,
    textAlign: "center",
  },
  noteRow: {
    flexDirection: "row",
    fontSize: 9,
    borderTop: "1pt solid #000000",
  },
  noteCol1: {
    width: "43%",
    borderRight: "1pt solid #000000",
    padding: 4,
    fontWeight: "bold",
  },
  noteCol2: {
    width: "57%",
    padding: 4,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  declarationText: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 8,
    textAlign: "justify",
  },
  footerSection: {
    marginTop: 5,
  },
  footerRow: {
    flexDirection: "row",
    marginBottom: 2,
    fontSize: 10,
  },
  footerLabel: {
    fontWeight: "bold",
    marginRight: 5,
  },
  signatureSection: {
    marginTop: 10,
  },
  signatureLabel: {
    fontSize: 10,
    marginBottom: 5,
  },
  signatureBox: {
    border: "1pt solid #000000",
    width: 180,
    height: 50,
    marginTop: 4,
  },
});

// Number to words function (English)
const numberToWords = (num) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if (num === 0) return "Zero";

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100)
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "");
    return (
      ones[Math.floor(n / 100)] +
      " Hundred" +
      (n % 100 !== 0 ? " " + convertLessThanThousand(n % 100) : "")
    );
  };

  const convert = (n) => {
    if (n === 0) return "";
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000)
      return (
        convertLessThanThousand(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 !== 0 ? " " + convertLessThanThousand(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        convertLessThanThousand(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 !== 0 ? " " + convert(Math.floor(n / 100000)) : "")
      );
    return (
      convertLessThanThousand(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 !== 0 ? " " + convert(Math.floor(n / 10000000)) : "")
    );
  };

  return convert(Math.floor(num));
};

// Purchase Agreement PDF Component
const PurchaseAgreementPDF = ({ purchaseData }) => {
  // Debug logging
  // console.log("PurchaseAgreementPDF rendered with data:", purchaseData);
  
  if (!purchaseData) {
    console.warn("⚠️ PurchaseAgreementPDF: No purchaseData provided");
    return null;
  }
  
  const data = purchaseData;
  
  // Validate required data
  if (!data.items || !Array.isArray(data.items) || data.items.length === 0) {
    console.warn("⚠️ PurchaseAgreementPDF: No items found in purchaseData");
  }

  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile =
    branchDetails.mobile || branchDetails.phone || "-";
  const companyGstin =
    branchDetails.gst_number ||
    branchDetails.gstin ||
    branchDetails.GST_number ||
    "-";
  const companyNameUpper = getTenantNameUpper();

  // Calculate totals
  const totalGwt = data.items?.reduce((sum, item) => sum + (parseFloat(item.gwt) || 0), 0) || 0;
  const totalNwt = data.items?.reduce((sum, item) => sum + (parseFloat(item.nwt) || 0), 0) || 0;
  const totalAmount = data.items?.reduce((sum, item) => sum + (parseFloat(item.total_amt) || 0), 0) || 0;

  // Format date and time
  const formatDateTime = (dateString) => {
    let date;
    if (!dateString) {
      date = new Date();
    } else {
      // Handle DD/MM/YYYY format
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          // DD/MM/YYYY format
          date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      // Check if date is valid
      if (isNaN(date.getTime())) {
        date = new Date();
      }
    }
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    
    return {
      date: `${day}/${month}/${year}`,
      time: `${String(displayHours).padStart(2, "0")}:${minutes}:${seconds}${ampm}`,
    };
  };

  const dateTime = formatDateTime(data.date || data.bill_date);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.companyTitle}>{companyNameUpper}</Text>
            <Text style={styles.mobileText}>Mobile: {companyMobile}</Text>
          </View>

          {/* Title Section */}
          <View style={styles.titleSection}>
            <View>
              <Text style={styles.titleText}>Purchase Agreement</Text>
            </View>
            <Text style={styles.gstText}>GST: {companyGstin}</Text>
          </View>

          {/* Seller Information */}
          <View style={styles.sellerSection}>
            <Text style={styles.sellerText}>
              I, the undersigned {data.customer?.name || "N/A"}, resident of {data.customer?.city || "AHMEDABAD"}
            </Text>
                <Text style={styles.sellerAddress}>
              Address: {data.customer?.address || "Nikol"}
                </Text>
                <Text style={styles.sellerMobile}>
              Mobile: {data.customer?.mobile || "9909217185"}
                </Text>
            <Text style={styles.sellerText}>
              am selling my own gold/jewellery at today's market rate and the sale price is Rs. {totalAmount.toFixed(2)} (Rupees {numberToWords(totalAmount)} Only).
            </Text>
            <Text style={styles.sellerText}>
              Details are as follows.
            </Text>
          </View>

          {/* Table */}
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCol1}>Sr. No.</Text>
              <Text style={styles.tableCol2}>Details</Text>
              <Text style={styles.tableCol3}>Pieces</Text>
              <Text style={styles.tableCol4}>Gross Weight</Text>
              <Text style={styles.tableCol5}>Net Weight</Text>
              <Text style={styles.tableCol6}>Rate</Text>
              <Text style={styles.tableCol7}>Total Amount</Text>
            </View>

            {Array.from({ length: 12 }).map((_, index) => {
              const item = data.items?.[index];
              if (item) {
              const itemRate = item.rate || (item.nwt > 0 ? item.total_amt / item.nwt : 0);
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCol1}>{index + 1}</Text>
                  <Text style={styles.tableCol2}>{item.item_name || ""}</Text>
                  <Text style={styles.tableCol3}>{item.pcs || "0"}</Text>
                  <Text style={styles.tableCol4}>
                    {parseFloat(item.gwt || 0).toFixed(3)}
                  </Text>
                  <Text style={styles.tableCol5}>
                    {parseFloat(item.nwt || 0).toFixed(3)}
                  </Text>
                  <Text style={styles.tableCol6}>
                    {itemRate.toFixed(0)}
                  </Text>
                  <Text style={styles.tableCol7}>
                    {parseFloat(item.total_amt || 0).toFixed(2)}
                  </Text>
                </View>
              );
              } else {
                // Empty row - no border, just space
                return (
                  <View key={index} style={{ flexDirection: "row", fontSize: 9, minHeight: 25 }}>
                    <Text style={styles.tableCol1}> </Text>
                    <Text style={styles.tableCol2}> </Text>
                    <Text style={styles.tableCol3}> </Text>
                    <Text style={styles.tableCol4}> </Text>
                    <Text style={styles.tableCol5}> </Text>
                    <Text style={styles.tableCol6}> </Text>
                    <Text style={styles.tableCol7}> </Text>
                  </View>
                );
              }
            })}

            {/* Note Row - Last row of table */}
            <View style={styles.noteRow}>
              <View style={styles.noteCol1}>
                <Text>Note:</Text>
          </View>
              <View style={styles.noteCol2}>
              <Text>{totalNwt.toFixed(3)}</Text>
              <Text>{totalAmount.toFixed(0)}</Text>
              </View>
            </View>
          </View>

          {/* Declaration Text */}
          <Text style={styles.declarationText}>
            This gold is my own property. And I have sold this gold to the trader at market rate with my full consent, without any coercion or threat from any side, and without using any intoxicating substance, having seen, known and heard its weight and price in my conscious state. Since I have received the sale price of this gold, I assure the trader that I will cooperate with the trader's wishes regarding the use of this gold. And I guarantee that I, the seller, will be fully responsible for resolving any legal complications related to the gold sold. Therefore, after receiving the money for the sale of gold as per the details mentioned above, I sign this declaration letter.
          </Text>

          {/* Footer Section */}
          <View style={styles.footerSection}>
            <View style={styles.footerRow}>
              <Text style={styles.footerLabel}>Place:</Text>
              <Text>{data.customer?.city || "AHMEDABAD"}</Text>
            </View>
            <View style={styles.footerRow}>
              <Text style={styles.footerLabel}>Date:</Text>
              <Text>{dateTime.date}</Text>
            </View>
            <View style={styles.footerRow}>
              <Text style={styles.footerLabel}>Time:</Text>
              <Text>{dateTime.time}</Text>
            </View>
          </View>

          {/* Signature Section */}
          <View style={styles.signatureSection}>
            <Text style={styles.signatureLabel}>
              Seller's Signature or Thumb Impression:
            </Text>
            <View style={{ flexDirection: "row", justifyContent: "flex-start", marginTop: 10, gap: 20 }}>
              <View style={styles.signatureBox} />
              <View style={styles.signatureBox} />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PurchaseAgreementPDF;


